import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box, Typography, Button, LinearProgress, Paper, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup,
  FormControlLabel, Radio, useMediaQuery, useTheme, CircularProgress,
  List, ListItem, ListItemButton, ListItemText, Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import NavigationIcon from '@mui/icons-material/Navigation';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import WmsBarcodeScanner from '../../shared/WmsBarcodeScanner';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import { waveService } from '../../services/waveService';
import {
  parseRouteAlternatives,
  type RoutingStrategy,
  type RouteAlternative,
  type ConsolidatedPickListItem,
} from '../../services/waveService';
import { locationService } from '../../services/locationService';
import PickerNavMap from './PickerNavMap';
import RouteAlternativesBar from './RouteAlternativesBar';
import CancelOrderDialog, { type CancelOrderResult } from './CancelOrderDialog';
import type { PickListItem } from './PickingActiveFlow';
import { matchesScannedValue, matchesScannedLocation } from '../../shared/scanMatching';

interface LocationNodeLite {
  id: string;
  code?: string;
  name?: string;
  barcodeData?: string;
}

interface PickingConsolidatedFlowProps {
  waveId: string;
  waveNumber: string;
  cartBarcode: string;
  warehouseId?: string;
  routeStrategy: RoutingStrategy;
  onRouteStrategyChange: (strategy: RoutingStrategy) => void;
  onComplete: (pickedCount: number, shortPickCount: number) => void;
  // Konsolide endpoint sunucuda yoksa (404), ust bilesen kalem-kalem akisa dussun.
  onConsolidatedUnavailable?: () => void;
  cancelledById?: string;
  cancelledByName?: string;
  onOrderCancelled?: (result: CancelOrderResult) => void;
}

type PickStep = 'scan-location' | 'scan-product' | 'confirm-quantity';

const isDoneStatus = (status: string) =>
  status === 'Picked' || status === 'ShortPick' || status === 'Sorted' || status === 'Cancelled';

const PickingConsolidatedFlow: React.FC<PickingConsolidatedFlowProps> = ({
  waveId, waveNumber, cartBarcode, warehouseId, routeStrategy, onRouteStrategyChange, onComplete,
  onConsolidatedUnavailable, cancelledById, cancelledByName, onOrderCancelled,
}) => {
  const { t } = useTranslation('warehouse');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { notification, notify, clear } = useWmsNotification();

  const [items, setItems] = useState<ConsolidatedPickListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [step, setStep] = useState<PickStep>('scan-location');
  // Operatörün fiilen okuttuğu lokasyon (öneri sadece ipucu; backend her geçerli
  // toplanabilir lokasyonu kabul eder). Toplama isteğinde bu ID gönderilir.
  const [scannedLocationId, setScannedLocationId] = useState<string>('');
  const [scannedLocationCode, setScannedLocationCode] = useState<string>('');
  const [locationIndex, setLocationIndex] = useState<Map<string, LocationNodeLite>>(new Map());
  const [quantityInput, setQuantityInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [shortPickOpen, setShortPickOpen] = useState(false);
  const [cancelSelectOpen, setCancelSelectOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<{ orderId: string; orderNumber?: string } | null>(null);
  const [shortPickMode, setShortPickMode] = useState<'receive' | 'partial' | 'skip'>('receive');
  const [partialQty, setPartialQty] = useState('');
  const [receiveQty, setReceiveQty] = useState('');
  // Lokasyonu olmayan ürün için öneri/atama durumu.
  const [locSuggestion, setLocSuggestion] = useState<{ suggestedLocationId?: string; locationCode?: string; locationName?: string; reason?: string } | null>(null);
  const [locLoading, setLocLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [routeAlternatives, setRouteAlternatives] = useState<RouteAlternative[]>([]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await waveService.getPickListConsolidated(waveId, routeStrategy);
      const payload = res.data || {};
      const data: ConsolidatedPickListItem[] = payload.data || payload.Data || [];
      setItems(data);
      setRouteAlternatives(parseRouteAlternatives(payload));
      const selected = (payload.strategy ?? payload.Strategy) as RoutingStrategy | undefined;
      if (selected && selected !== routeStrategy) {
        onRouteStrategyChange(selected);
      }
      const idx = data.findIndex(i => !isDoneStatus(i.status));
      setCurrentIndex(idx >= 0 ? idx : 0);
    } catch (err: any) {
      // 404: konsolide endpoint sunucuda yok (API guncellenmemis). Kalem-kalem
      // akisa dusmesi icin ust bilesene haber ver; hata ekrani gosterme.
      if (err?.response?.status === 404 && onConsolidatedUnavailable) {
        setLoading(false);
        onConsolidatedUnavailable();
        return;
      }
      // Diger hatalar (or. DB baglanti/timeout). Hatayi yutup bos liste gostermek
      // "tum urunler toplandi" yaniltmasina yol aciyordu; bunun yerine hatayi
      // gosterip kullaniciya tekrar deneme imkani veriyoruz.
      setItems([]);
      setLoadError(
        err?.response?.data?.message
        || err?.response?.data?.Message
        || err?.message
        || t('screens.picking.consolidatedFlow.loadFailed'),
      );
    }
    setLoading(false);
  }, [waveId, routeStrategy, onRouteStrategyChange, onConsolidatedUnavailable, t]);

  useEffect(() => { loadList(); }, [loadList]);

  // Okutulan lokasyon kodunu ID'ye çözmek için depo lokasyonlarını indeksle.
  useEffect(() => {
    if (!warehouseId) { setLocationIndex(new Map()); return; }
    let cancelled = false;
    (async () => {
      try {
        const res: any = await locationService.getByWarehouse(warehouseId);
        const rows = res?.data?.data || res?.data?.Data || res?.data || [];
        const next = new Map<string, LocationNodeLite>();
        for (const raw of rows) {
          const id = String(raw?.Id || raw?.id || '').trim();
          if (!id) continue;
          next.set(id, {
            id,
            code: String(raw?.Code || raw?.code || '').trim(),
            name: String(raw?.Name || raw?.name || '').trim(),
            barcodeData: String(raw?.BarcodeData || raw?.barcodeData || '').trim(),
          });
        }
        if (!cancelled) setLocationIndex(next);
      } catch {
        if (!cancelled) setLocationIndex(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, [warehouseId]);

  const currentItem = items[currentIndex];
  const remainingQty = currentItem ? Math.max(0, currentItem.totalQuantity - currentItem.pickedQuantity) : 0;
  // Ürünün tanımlı lokasyonu yoksa önce lokasyon atanmalı (aksi halde okutulacak lokasyon yok).
  const needsLocation = !!currentItem && !currentItem.locationId;

  // Lokasyonu olmayan ürüne geçildiğinde otomatik lokasyon önerisi getir.
  useEffect(() => {
    if (!currentItem || !needsLocation) { setLocSuggestion(null); return; }
    let cancelled = false;
    setLocLoading(true);
    setLocSuggestion(null);
    waveService.getLocationSuggestion(waveId, currentItem.productId)
      .then(res => { if (!cancelled) setLocSuggestion(res.data?.data || null); })
      .catch(() => { if (!cancelled) setLocSuggestion(null); })
      .finally(() => { if (!cancelled) setLocLoading(false); });
    return () => { cancelled = true; };
  }, [currentItem, needsLocation, waveId]);

  const { doneCount, progress } = useMemo(() => {
    const done = items.filter(i => isDoneStatus(i.status)).length;
    return { doneCount: done, progress: items.length > 0 ? (done / items.length) * 100 : 0 };
  }, [items]);

  // Nav harita için konsolide kalemleri PickListItem benzeri yapıya dönüştür.
  const mapPickList: PickListItem[] = useMemo(() => items.map(i => ({
    orderItemId: i.productId,
    orderId: '',
    productId: i.productId,
    productName: i.productName,
    sku: i.sku,
    barcode: i.barcode,
    quantity: i.totalQuantity,
    pickedQuantity: i.pickedQuantity,
    locationId: i.locationId,
    locationCode: i.locationCode,
    locationName: i.locationName,
    sortOrder: i.sortOrder,
    status: i.status,
    aisleId: i.aisleId,
    aisleName: i.aisleName,
    aisleSide: i.aisleSide,
    gridX: i.gridX,
    gridY: i.gridY,
  })), [items]);

  const advanceFrom = useCallback((updated: ConsolidatedPickListItem[], fromIndex: number) => {
    const nextIdx = updated.findIndex((item, idx) => idx > fromIndex && !isDoneStatus(item.status));
    if (nextIdx >= 0) {
      setCurrentIndex(nextIdx);
      setStep('scan-location');
      return;
    }
    const anyLeft = updated.some(i => !isDoneStatus(i.status));
    if (!anyLeft) {
      onComplete(
        updated.filter(i => i.status === 'Picked').length,
        updated.filter(i => i.status === 'ShortPick').length,
      );
    } else {
      const firstLeft = updated.findIndex(i => !isDoneStatus(i.status));
      setCurrentIndex(firstLeft);
      setStep('scan-location');
    }
  }, [onComplete]);

  const openCancelForOrder = useCallback((orderId: string, orderNumber?: string) => {
    setCancelSelectOpen(false);
    setCancelTarget({ orderId, orderNumber });
  }, []);

  const handleCancelClick = useCallback(() => {
    if (!currentItem?.orders?.length) return;
    if (currentItem.orders.length === 1) {
      openCancelForOrder(currentItem.orders[0].orderId, currentItem.orders[0].orderNumber);
      return;
    }
    setCancelSelectOpen(true);
  }, [currentItem, openCancelForOrder]);

  const handleOrderCancelled = useCallback(async (result: CancelOrderResult) => {
    setCancelTarget(null);
    notify({
      type: 'warning',
      message: result.waveStillActive && result.remainingPendingItems > 0
        ? t('screens.picking.cancel.successContinue')
        : t('screens.picking.cancel.successWaveDone'),
      duration: 2500,
    });
    onOrderCancelled?.(result);
    if (!result.waveStillActive || result.remainingPendingItems <= 0) {
      return;
    }
    try {
      await loadList();
    } catch {
      // loadList kendi hatalarını yönetir
    }
  }, [notify, t, onOrderCancelled, loadList]);

  const handleLocationScan = useCallback((barcode: string) => {
    if (!currentItem) return;
    const expected = currentItem.locationCode || '';

    // 1) Önerilen (pick-list) lokasyonuyla eşleşiyor mu?
    let resolvedId = '';
    let resolvedCode = '';
    if (matchesScannedLocation(barcode, expected) || matchesScannedLocation(barcode, currentItem.locationId)) {
      resolvedId = currentItem.locationId || '';
      resolvedCode = expected;
    } else {
      // 2) Farklı bir lokasyon okutuldu. Backend ürünün stoğu olan herhangi geçerli
      //    toplanabilir lokasyonu kabul ettiği için okutulan kodu ID'ye çözüyoruz.
      const match = Array.from(locationIndex.values()).find(
        node => matchesScannedLocation(barcode, node.code)
          || matchesScannedLocation(barcode, node.barcodeData)
          || matchesScannedLocation(barcode, node.name)
          || matchesScannedLocation(barcode, node.id)
      );
      if (match) {
        resolvedId = match.id;
        resolvedCode = match.code || '';
      }
    }

    if (!resolvedId) {
      notify({ type: 'error', message: t('screens.picking.common.locationNotRecognized'), duration: 4000 });
      return;
    }

    setScannedLocationId(resolvedId);
    setScannedLocationCode(resolvedCode);
    setStep('scan-product');

    if (currentItem.locationId && resolvedId !== currentItem.locationId) {
      notify({ type: 'info', message: t('screens.picking.common.locationDifferentAccepted', { code: resolvedCode || barcode }), duration: 2500 });
    } else {
      notify({ type: 'success', message: t('screens.picking.common.locationVerified'), duration: 1500 });
    }
  }, [currentItem, locationIndex, notify, t]);

  const handleProductScan = useCallback((barcode: string) => {
    if (!currentItem) return;
    if (
      matchesScannedValue(barcode, currentItem.barcode) ||
      matchesScannedValue(barcode, currentItem.sku) ||
      matchesScannedValue(barcode, currentItem.productId)
    ) {
      setQuantityInput(String(remainingQty));
      setStep('confirm-quantity');
      notify({ type: 'success', message: t('screens.picking.common.productVerified'), duration: 1500 });
    } else {
      const expectedLabel = String(currentItem.barcode || currentItem.sku || currentItem.productName || '').trim();
      notify({ type: 'error', message: t('screens.picking.common.wrongProduct', { expected: expectedLabel }), duration: 4000 });
    }
  }, [currentItem, remainingQty, notify, t]);

  const handleConfirm = useCallback(async (qty: number) => {
    if (!currentItem || saving) return;
    setSaving(true);
    try {
      const res = await waveService.pickConsolidated(waveId, {
        ProductId: currentItem.productId,
        ScannedLocationId: scannedLocationId || currentItem.locationId,
        PickedQuantity: qty,
        IsShortPick: false,
      });
      const distributed: number = res.data?.data?.distributedQuantity ?? qty;
      const updated = [...items];
      const newPicked = currentItem.pickedQuantity + distributed;
      const fullyPicked = newPicked >= currentItem.totalQuantity;
      updated[currentIndex] = {
        ...currentItem,
        pickedQuantity: newPicked,
        // Kismi toplandiysa kalem ACIK kalir (ShortPick DEGIL) — dalga otomatik tamamlanmaz.
        status: fullyPicked ? 'Picked' : (newPicked > 0 ? 'Picking' : 'Pending'),
      };
      setItems(updated);

      if (fullyPicked) {
        notify({ type: 'success', message: t('screens.picking.common.pickedToast', { done: doneCount + 1, total: items.length }), duration: 2000 });
        advanceFrom(updated, currentIndex);
      } else {
        // Lokasyonda yeterli stok yok: kullaniciyi cozum diyaloguna yonlendir.
        const missing = currentItem.totalQuantity - newPicked;
        notify({
          type: 'warning',
          message: t('screens.picking.consolidatedFlow.notEnoughStock', { picked: distributed, missing }),
          duration: 3500,
        });
        setShortPickMode('receive');
        setReceiveQty(String(missing));
        setPartialQty(String(newPicked));
        setShortPickOpen(true);
      }
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || t('screens.picking.common.pickSaveFailed') });
    }
    setSaving(false);
  }, [currentItem, currentIndex, items, waveId, doneCount, saving, notify, advanceFrom, t, scannedLocationId]);

  // Yerinde mal kabul: eksik stogu lokasyona gir, ardindan kalani otomatik topla.
  // Lokasyonu olmayan üründe hedef olarak önerilen lokasyon kullanılır.
  const handleReceiveStock = useCallback(async () => {
    if (!currentItem || saving) return;
    const targetLoc = currentItem.locationId || locSuggestion?.suggestedLocationId;
    if (!targetLoc) {
      notify({ type: 'error', message: t('screens.picking.common.noStockLocation') });
      return;
    }
    const qty = parseInt(receiveQty) || 0;
    if (qty <= 0) {
      notify({ type: 'error', message: t('screens.picking.common.qtyMustBePositive') });
      return;
    }
    setSaving(true);
    try {
      await waveService.adjustStock(waveId, {
        ProductId: currentItem.productId,
        LocationId: targetLoc,
        Quantity: qty,
        Reason: t('screens.picking.common.adjustReason'),
      });
      // Stok girildi; kalan miktari topla.
      const res = await waveService.pickConsolidated(waveId, {
        ProductId: currentItem.productId,
        ScannedLocationId: targetLoc,
        PickedQuantity: remainingQty,
        IsShortPick: false,
      });
      const distributed: number = res.data?.data?.distributedQuantity ?? 0;
      const updated = [...items];
      const newPicked = currentItem.pickedQuantity + distributed;
      const fullyPicked = newPicked >= currentItem.totalQuantity;
      updated[currentIndex] = {
        ...currentItem,
        pickedQuantity: newPicked,
        status: fullyPicked ? 'Picked' : (newPicked > 0 ? 'Picking' : 'Pending'),
      };
      setItems(updated);
      setShortPickOpen(false);
      const hadNoLocation = !currentItem.locationId;
      if (fullyPicked) {
        notify({ type: 'success', message: t('screens.picking.consolidatedFlow.stockReceivedAndPicked'), duration: 2000 });
        advanceFrom(updated, currentIndex);
      } else {
        notify({ type: 'warning', message: t('screens.picking.consolidatedFlow.stillMissing', { count: currentItem.totalQuantity - newPicked }), duration: 3000 });
        // Lokasyonsuz üründe kalan varsa listeyi tazele ki lokasyon bilgisi otursun.
        if (hadNoLocation) await loadList();
      }
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || t('screens.picking.common.stockSaveFailed') });
    }
    setSaving(false);
  }, [currentItem, currentIndex, items, waveId, receiveQty, remainingQty, saving, notify, advanceFrom, locSuggestion, loadList, t]);

  // Önerilen (veya seçilen) lokasyona lokasyonsuz stoğu ata; ardından listeyi tazele.
  const handleAssignLocation = useCallback(async (locationId?: string) => {
    if (!currentItem || assigning) return;
    if (!locationId) {
      notify({ type: 'error', message: t('screens.picking.common.noLocationToAssign') });
      return;
    }
    setAssigning(true);
    try {
      const res = await waveService.assignLocation(waveId, {
        ProductId: currentItem.productId,
        LocationId: locationId,
      });
      const data = res.data?.data;
      if (data?.assigned) {
        notify({ type: 'success', message: t('screens.picking.common.locationAssigned', { code: data.locationCode || '' }), duration: 2000 });
        await loadList();
      } else if (data?.requiresReceiving) {
        // Taşınacak stok yok → yerinde mal kabul akışına yönlendir (önerilen lokasyona).
        notify({ type: 'info', message: t('screens.picking.common.requiresReceiving'), duration: 3000 });
        setShortPickMode('receive');
        setReceiveQty(String(remainingQty));
        setShortPickOpen(true);
      } else {
        notify({ type: 'warning', message: data?.message || t('screens.picking.common.assignFailedSoft') });
      }
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || t('screens.picking.common.assignFailed') });
    }
    setAssigning(false);
  }, [currentItem, waveId, assigning, remainingQty, notify, loadList, t]);

  const handleShortPick = useCallback(async () => {
    if (!currentItem || saving) return;
    setSaving(true);
    try {
      const partial = shortPickMode === 'partial' ? parseInt(partialQty) || 0 : 0;
      const res = await waveService.pickConsolidated(waveId, {
        ProductId: currentItem.productId,
        ScannedLocationId: scannedLocationId || currentItem.locationId,
        PickedQuantity: partial,
        IsShortPick: true,
        Reason: shortPickMode === 'partial' ? t('screens.picking.consolidatedFlow.reasonPartial') : t('screens.picking.consolidatedFlow.reasonSkip'),
      });
      const distributed: number = res.data?.data?.distributedQuantity ?? partial;
      const updated = [...items];
      const newPicked = currentItem.pickedQuantity + distributed;
      updated[currentIndex] = {
        ...currentItem,
        pickedQuantity: newPicked,
        status: newPicked >= currentItem.totalQuantity ? 'Picked' : 'ShortPick',
      };
      setItems(updated);
      setShortPickOpen(false);
      notify({ type: 'warning', message: t('screens.picking.common.shortPickSaved'), duration: 2000 });
      advanceFrom(updated, currentIndex);
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || t('screens.picking.common.shortPickSaveFailed') });
    }
    setSaving(false);
  }, [currentItem, currentIndex, items, waveId, shortPickMode, partialQty, saving, notify, advanceFrom, t, scannedLocationId]);

  const mapHeight = isMobile ? 168 : 200;
  const locationSubtitle = useMemo(() => {
    if (!currentItem) return '';
    return [currentItem.locationName, currentItem.aisleName].filter(Boolean).join(' · ');
  }, [currentItem]);

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  if (loadError) {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center', maxWidth: 460, mx: 'auto' }}>
        <WarningIcon sx={{ fontSize: 56, color: 'error.main', mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{t('screens.picking.consolidatedFlow.loadFailedTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{loadError}</Typography>
        <Button variant="contained" size="large" onClick={loadList} sx={{ minWidth: 180, height: 52 }}>
          {t('screens.picking.consolidatedFlow.retry')}
        </Button>
      </Box>
    );
  }

  if (!currentItem) {
    // Buraya yalnizca liste tamamen bos geldiginde duser (currentIndex 0'a sabitlenir).
    // Yeni baslatilan bir is emrinde bu bir anomalidir ("toplandi" demek yaniltici olur),
    // bu yuzden net bir mesaj + yeniden yukleme sunuyoruz.
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center', maxWidth: 460, mx: 'auto' }}>
        <Inventory2Icon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{t('screens.picking.consolidatedFlow.noItemsTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('screens.picking.consolidatedFlow.noItemsMessage')}
        </Typography>
        <Button variant="contained" size="large" onClick={loadList} sx={{ minWidth: 180, height: 52 }}>
          {t('screens.picking.consolidatedFlow.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <WmsNotification notification={notification} onClose={clear} />

      <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', flexShrink: 0 }}>
        <LinearProgress variant="determinate" value={progress} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {t('screens.picking.common.progressCount', { done: doneCount, total: items.length })}
        </Typography>
        <Chip size="small" color="secondary" label={t('screens.picking.consolidatedFlow.consolidatedChip')} variant="outlined" sx={{ height: 24 }} />
        {cartBarcode && <Chip size="small" label={t('screens.picking.common.cartLabel', { code: cartBarcode })} variant="outlined" sx={{ height: 24 }} />}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1.5, pt: 1, pb: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {warehouseId && (
          <>
            <RouteAlternativesBar
              alternatives={routeAlternatives}
              selectedStrategy={routeStrategy}
              onSelect={onRouteStrategyChange}
            />
            <PickerNavMap
              warehouseId={warehouseId}
              pickList={mapPickList}
              currentIndex={currentIndex}
              height={mapHeight}
              locationSubtitle={locationSubtitle}
              ghostStops={(routeAlternatives.find(a => a.stopOrder?.length)?.stopOrder || [])
                .filter(s => s.gridX != null && s.gridY != null)
                .map(s => ({ gridX: s.gridX as number, gridY: s.gridY as number }))}
            />
          </>
        )}

        {/* Ürün Kartı — kompakt */}
        <Paper variant="outlined" sx={{ p: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Inventory2Icon color="secondary" sx={{ fontSize: 18 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
              {t('screens.picking.consolidatedFlow.toBePickedConsolidated')}
            </Typography>
          </Box>
          <Typography sx={{ fontWeight: 700, mt: 0.25, fontSize: '0.95rem', lineHeight: 1.25 }}>
            {currentItem.productName || t('screens.picking.common.productFallback')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('screens.picking.common.barcodeValue', { value: currentItem.barcode || currentItem.sku || '-' })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" color="primary" label={t('screens.picking.consolidatedFlow.totalPieces', { count: currentItem.totalQuantity })} sx={{ height: 24 }} />
            <Chip size="small" variant="outlined" label={t('screens.picking.consolidatedFlow.distributeToOrders', { count: currentItem.orderCount })} sx={{ height: 24 }} />
            {currentItem.pickedQuantity > 0 && (
              <Chip size="small" color="success" variant="outlined" label={t('screens.picking.consolidatedFlow.pickedChip', { count: currentItem.pickedQuantity })} sx={{ height: 24 }} />
            )}
          </Box>

          <Button
            size="small"
            onClick={() => setOrdersOpen(v => !v)}
            endIcon={ordersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mt: 0.5, px: 0.5, minHeight: 32, fontSize: '0.75rem', color: 'text.secondary' }}
          >
            {t('screens.picking.consolidatedFlow.orderDistribution')}
          </Button>
          <Collapse in={ordersOpen}>
            <List dense sx={{ maxHeight: 100, overflow: 'auto', py: 0 }}>
              {currentItem.orders.map(o => (
                <ListItem key={o.orderItemId} disablePadding sx={{ py: 0.15 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={500}>{o.orderNumber || o.orderId.slice(0, 8)}</Typography>
                        <Chip size="small" variant="outlined" label={t('screens.picking.consolidatedFlow.piecesShort', { count: o.quantity })} sx={{ height: 18, fontSize: '0.65rem' }} />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Paper>

        {needsLocation && (
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'info.light', bgcolor: 'rgba(2, 136, 209, 0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AddLocationAltIcon color="info" fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('screens.picking.common.noLocationDefined')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('screens.picking.consolidatedFlow.assignFirst')}
            </Typography>

            {locLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={18} /><Typography variant="body2">{t('screens.picking.consolidatedFlow.suggestionPreparing')}</Typography>
              </Box>
            ) : locSuggestion?.suggestedLocationId ? (
              <>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'info.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <NavigationIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>{t('screens.picking.common.suggestedLocation')}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {locSuggestion.locationName || locSuggestion.locationCode}
                    </Typography>
                    {locSuggestion.reason && (
                      <Typography variant="caption" color="text.secondary">{locSuggestion.reason}</Typography>
                    )}
                  </Box>
                </Paper>
                <Button
                  variant="contained"
                  color="info"
                  fullWidth
                  disabled={assigning}
                  onClick={() => handleAssignLocation(locSuggestion?.suggestedLocationId)}
                  sx={{ height: 48 }}
                >
                  {assigning ? '...' : t('screens.picking.common.assignLocationTo', { code: locSuggestion.locationCode })}
                </Button>
              </>
            ) : (
              <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                {locSuggestion?.reason || t('screens.picking.consolidatedFlow.noSuggestionManual')}
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Barkod okutma — her zaman görünür (scroll dışında) */}
      {!needsLocation && step === 'scan-location' && (
        <Box sx={{ flexShrink: 0, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 700 }}>
            {t('screens.picking.common.scanLocationStep')}
          </Typography>
          <WmsBarcodeScanner
            onScan={handleLocationScan}
            placeholder={t('screens.picking.common.scanLocationPlaceholder')}
            size="medium"
          />
        </Box>
      )}

      {!needsLocation && step === 'scan-product' && (
        <Box sx={{ flexShrink: 0, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
              {t('screens.picking.common.locationVerifiedAt', { code: scannedLocationCode || currentItem.locationCode })}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 700 }}>{t('screens.picking.common.scanProductStep')}</Typography>
          <WmsBarcodeScanner onScan={handleProductScan} placeholder={t('screens.picking.common.scanProductPlaceholder')} size="medium" />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: 'block' }}>
            {t('screens.picking.common.expectedWithName', { value: currentItem.barcode || currentItem.sku || '-', name: currentItem.productName })}
          </Typography>
        </Box>
      )}

      {!needsLocation && step === 'confirm-quantity' && (
        <Box sx={{ flexShrink: 0, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
            <Typography variant="caption" color="success.main">{t('screens.picking.common.productVerifiedShort')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 700 }}>
            {t('screens.picking.consolidatedFlow.qty3ConfirmTotal', { count: remainingQty })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              type="number"
              value={quantityInput}
              onChange={e => setQuantityInput(e.target.value)}
              sx={{ width: 110 }}
              inputProps={{ min: 1, max: remainingQty }}
            />
            <Button
              variant="contained"
              onClick={() => handleConfirm(Math.min(parseInt(quantityInput) || remainingQty, remainingQty))}
              disabled={saving}
              sx={{ height: 40 }}
            >
              {saving ? '...' : t('common.confirm')}
            </Button>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: 1,
          pt: 1,
          display: 'flex',
          gap: { xs: 0.75, sm: 1 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<SkipNextIcon />}
          onClick={() => advanceFrom(items, currentIndex)}
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 0.5, sm: 1.5 },
            fontSize: { xs: '0.72rem', sm: '0.875rem' },
            '& .MuiButton-startIcon': { mr: { xs: 0.35, sm: 0.75 } },
          }}
        >
          {t('screens.picking.common.skip')}
        </Button>
        <Button
          variant="outlined"
          color="warning"
          startIcon={<WarningIcon />}
          onClick={() => { setShortPickOpen(true); setShortPickMode('receive'); setPartialQty(''); setReceiveQty(String(remainingQty)); }}
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 0.5, sm: 1.5 },
            fontSize: { xs: '0.72rem', sm: '0.875rem' },
            '& .MuiButton-startIcon': { mr: { xs: 0.35, sm: 0.75 } },
          }}
        >
          {t('screens.picking.common.stockIssue')}
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelOutlinedIcon />}
          onClick={handleCancelClick}
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 0.5, sm: 1.5 },
            fontSize: { xs: '0.72rem', sm: '0.875rem' },
            '& .MuiButton-startIcon': { mr: { xs: 0.35, sm: 0.75 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('screens.picking.cancel.button')}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            {t('common.cancel')}
          </Box>
        </Button>
      </Box>

      <Dialog open={shortPickOpen} onClose={() => setShortPickOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('screens.picking.common.stockIssue')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('screens.picking.common.stockIssueProduct')} <strong>{currentItem.productName}</strong></Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('screens.picking.common.stockIssueLocation')} <strong>{currentItem.locationCode || t('screens.picking.common.undefinedLocation')}</strong></Typography>
          <Typography variant="body2" sx={{ mb: 2 }}><strong>{t('screens.picking.consolidatedFlow.remainingRequired', { count: remainingQty })}</strong></Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('screens.picking.common.whatToDo')}</Typography>
          <RadioGroup value={shortPickMode} onChange={e => setShortPickMode(e.target.value as any)}>
            <FormControlLabel value="receive" control={<Radio />} label={t('screens.picking.consolidatedFlow.modeReceive')} />
            <FormControlLabel value="partial" control={<Radio />} label={t('screens.picking.consolidatedFlow.modePartial')} />
            <FormControlLabel value="skip" control={<Radio />} label={t('screens.picking.consolidatedFlow.modeSkip')} />
          </RadioGroup>

          {shortPickMode === 'receive' && (
            <>
              {!currentItem.locationId && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
                  {t('screens.picking.consolidatedFlow.noLocationForStock')}
                </Typography>
              )}
              <TextField
                size="small"
                type="number"
                label={t('screens.picking.consolidatedFlow.receiveQtyLabel')}
                value={receiveQty}
                onChange={e => setReceiveQty(e.target.value)}
                sx={{ mt: 1 }}
                inputProps={{ min: 1 }}
                disabled={!currentItem.locationId}
                fullWidth
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('screens.picking.consolidatedFlow.receiveHint', { code: currentItem.locationCode })}
              </Typography>
            </>
          )}

          {shortPickMode === 'partial' && (
            <TextField
              size="small"
              type="number"
              label={t('screens.picking.consolidatedFlow.partialQtyLabel')}
              value={partialQty}
              onChange={e => setPartialQty(e.target.value)}
              sx={{ mt: 1 }}
              inputProps={{ min: 0, max: remainingQty - 1 }}
              fullWidth
            />
          )}

          {shortPickMode === 'skip' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('screens.picking.consolidatedFlow.skipHint')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortPickOpen(false)}>{t('common.cancel')}</Button>
          {shortPickMode === 'receive' ? (
            <Button variant="contained" color="primary" onClick={handleReceiveStock} disabled={saving || !currentItem.locationId}>
              {saving ? '...' : t('screens.picking.consolidatedFlow.enterStockAndPick')}
            </Button>
          ) : (
            <Button variant="contained" color="warning" onClick={handleShortPick} disabled={saving}>
              {saving ? '...' : t('common.confirm')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={cancelSelectOpen} onClose={() => setCancelSelectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('screens.picking.cancel.title')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('screens.picking.cancel.selectOrder')}
          </Typography>
          <List dense>
            {(currentItem?.orders || []).map((o) => (
              <ListItem key={o.orderId} disablePadding>
                <ListItemButton onClick={() => openCancelForOrder(o.orderId, o.orderNumber)}>
                  <ListItemText
                    primary={o.orderNumber || o.orderId.slice(0, 8)}
                    secondary={t('screens.picking.consolidatedFlow.piecesShort', { count: o.quantity })}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelSelectOpen(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {cancelTarget && (
        <CancelOrderDialog
          open={!!cancelTarget}
          orderId={cancelTarget.orderId}
          orderNumber={cancelTarget.orderNumber}
          productName={currentItem?.productName}
          cancelledById={cancelledById}
          cancelledByName={cancelledByName}
          onClose={() => setCancelTarget(null)}
          onCancelled={handleOrderCancelled}
        />
      )}
    </Box>
  );
};

export default PickingConsolidatedFlow;
